import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

const categoryBanners: Record<string, string> = {
  romance: 'https://readdy.ai/api/search-image?query=Romantic%20Korean%20manhwa%20illustration%20banner%2C%20soft%20pink%20and%20peach%20gradient%20background%2C%20elegant%20couple%20silhouettes%2C%20floating%20petals%2C%20dreamy%20atmosphere%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-romance&orientation=landscape',
  fantasy: 'https://readdy.ai/api/search-image?query=Fantasy%20Korean%20manhwa%20illustration%20banner%2C%20mystical%20golden%20and%20teal%20background%2C%20magical%20floating%20elements%2C%20mysterious%20ancient%20ruins%2C%20dramatic%20lighting%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-fantasy&orientation=landscape',
  action: 'https://readdy.ai/api/search-image?query=Action%20Korean%20manhwa%20illustration%20banner%2C%20dynamic%20red%20and%20dark%20background%2C%20explosive%20energy%20effects%2C%20powerful%20silhouette%2C%20dramatic%20lighting%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-action&orientation=landscape',
  drama: 'https://readdy.ai/api/search-image?query=Drama%20Korean%20manhwa%20illustration%20banner%2C%20emotional%20warm%20amber%20and%20brown%20background%2C%20subtle%20melancholic%20atmosphere%2C%20urban%20cityscape%2C%20soft%20gradient%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-drama&orientation=landscape',
  thriller: 'https://readdy.ai/api/search-image?query=Thriller%20Korean%20manhwa%20illustration%20banner%2C%20mysterious%20dark%20blue%20background%2C%20shadowy%20figures%2C%20suspenseful%20atmosphere%2C%20fog%20effects%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-thriller&orientation=landscape',
  comedy: 'https://readdy.ai/api/search-image?query=Comedy%20Korean%20manhwa%20illustration%20banner%2C%20bright%20yellow%20and%20orange%20background%2C%20playful%20elements%2C%20cheerful%20atmosphere%2C%20fun%20style%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-comedy&orientation=landscape',
  horror: 'https://readdy.ai/api/search-image?query=Horror%20Korean%20manhwa%20illustration%20banner%2C%20eerie%20dark%20purple%20and%20black%20background%2C%20spooky%20atmosphere%2C%20creepy%20silhouettes%2C%20moonlight%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-horror&orientation=landscape',
  bl: 'https://readdy.ai/api/search-image?query=BL%20Korean%20manhwa%20illustration%20banner%2C%20soft%20lavender%20and%20blue%20background%2C%20romantic%20atmosphere%2C%20delicate%20floral%20elements%2C%20gentle%20lighting%2C%20clean%20minimal%20design%2C%20professional%20webtoon%20banner%20style&width=1200&height=400&seq=cat-bl&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryName = category?.name || slug || '';
  const categoryComics = comics.filter((c) => category && c.category === category.name);
  const bannerSrc = categoryBanners[slug || ''] || categoryBanners.romance;

  useEffect(() => {
    document.title = `${categoryName}漫画 - 韩国漫画大全 - 奇妙漫画网`;
  }, [categoryName]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '漫画分类', href: '/category/romance' },
    { label: categoryName },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}漫画 - 奇妙漫画网`,
    description: `奇妙漫画网${categoryName}漫画分类，收录${categoryComics.length}部精选${categoryName}韩国漫画，免费在线阅读。`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: '漫画分类', item: '/category/romance' },
        { '@type': 'ListItem', position: 3, name: categoryName },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-end pt-16">
          <div className="absolute inset-0">
            <img src={bannerSrc} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/30"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pb-10">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                  <h1 className="text-3xl md:text-5xl font-black text-background-50 font-heading">
                    {categoryName}漫画
                  </h1>
                  <p className="text-sm text-background-200/70 mt-2">
                    共收录 {categoryComics.length} 部{categoryName}韩国漫画
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6 lg:px-10">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 md:gap-6">
                {categoryComics.length > 0 ? (
                  categoryComics.map((comic) => (
                    <ComicCard
                      key={comic.id}
                      id={comic.id}
                      title={comic.title}
                      category={comic.category}
                      cover={comic.cover}
                      rating={comic.rating}
                      hot={comic.hot}
                      status={comic.status}
                      chapters={comic.chapters}
                    />
                  ))
                ) : (
                  <div className="col-span-full py-20 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                      <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}